import { useEffect, useMemo, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';

export const REPORT_API = `${process.env.REACT_APP_BACKEND_URL}/api/academico/relatorios`;
export const PRESET_LABELS = { simples: 'Simples', pedagogica: 'Pedagógica', completa: 'Completa', personalizada: 'Personalizada' };
const message = error => error.response?.data?.detail || error.userMessage || 'Não foi possível carregar o relatório.';

export const useReport = report => {
  const [options, setOptions] = useState(null);
  const [presets, setPresets] = useState({});
  const [mode, setMode] = useState(report === 'livro-classe' ? 'pedagogica' : 'simples');
  const [columns, setColumns] = useState([]);
  const [filters, setFilters] = useState({ ano_letivo: '', unidade: '', segmento: '', professor_id: '', ordenacao: 'nome', abrangencia: 'selecionadas', turma_ids: [] });
  const [offset, setOffset] = useState(0);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [reload, setReload] = useState(0);
  const [setupReload, setSetupReload] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setError('');
    Promise.all([api.get(`${REPORT_API}/filtros`, { signal: controller.signal }), api.get(`${REPORT_API}/presets/${report}`, { signal: controller.signal })])
      .then(([available, stored]) => {
        setOptions(available.data); setPresets(stored.data.presets);
        const initial = report === 'livro-classe' ? 'pedagogica' : 'simples';
        setMode(initial); setColumns(stored.data.presets[initial]);
        const currentYear = String(new Date().getFullYear());
        setFilters(previous => ({ ...previous, ano_letivo: available.data.anos.includes(currentYear) ? currentYear : available.data.anos[0] || '' }));
      }).catch(err => { if (!controller.signal.aborted) setError(message(err)); });
    return () => controller.abort();
  }, [report, setupReload]);

  const availableClasses = useMemo(() => (options?.turmas || []).filter(group => (
    (!filters.ano_letivo || group.year === filters.ano_letivo) && (!filters.unidade || group.unit === filters.unidade) &&
    (!filters.segmento || group.segment === filters.segmento) && (!filters.professor_id || options.professores.find(p => p.id === filters.professor_id)?.class_ids.includes(group.id))
  )), [options, filters]);
  const params = useMemo(() => ({ ...filters, turma_ids: filters.turma_ids.join(','), modo: mode, colunas: columns.join(','), offset, limit: 50 }), [filters, mode, columns, offset]);
  const key = JSON.stringify(params);
  const ready = !!options && columns.length > 0 && (filters.turma_ids.length > 0 || (filters.abrangencia === 'segmento' && !!filters.segmento));
  const preview = result?.key === key ? result.data : null;

  useEffect(() => {
    if (!ready) { setResult(null); setLoading(false); return; }
    const controller = new AbortController();
    setLoading(true); setError('');
    const timer = setTimeout(() => {
      api.get(`${REPORT_API}/${report}`, { params: JSON.parse(key), signal: controller.signal })
        .then(response => { if (!controller.signal.aborted) setResult({ key, data: response.data }); })
        .catch(err => { if (!controller.signal.aborted) { setResult(null); setError(message(err)); } })
        .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    }, 250);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [key, ready, reload, report]);

  const changeFilter = (field, value) => {
    setOffset(0); setError('');
    setFilters(previous => {
      const next = { ...previous, [field]: value };
      if (['ano_letivo', 'unidade', 'segmento', 'professor_id', 'abrangencia'].includes(field)) next.turma_ids = [];
      if (field === 'segmento' && !value) next.abrangencia = 'selecionadas';
      return next;
    });
  };
  const selectPreset = value => { setMode(value); setColumns(presets[value] || []); setOffset(0); };
  const toggleColumn = id => {
    setMode('personalizada'); setOffset(0);
    setColumns(previous => previous.includes(id) ? previous.filter(key => key !== id) : [...previous, id]);
  };
  const savePreset = async () => {
    setSaving(true);
    try {
      const response = await api.put(`${REPORT_API}/presets`, { report, preset: mode, columns });
      setPresets(response.data.presets); toast.success('Preset salvo para sua conta.');
    } catch (err) { toast.error(message(err)); }
    finally { setSaving(false); }
  };
  const refresh = () => !options ? setSetupReload(v => v + 1) : setReload(v => v + 1);
  return { options, filters, changeFilter, availableClasses, mode, columns, selectPreset, toggleColumn, savePreset, saving,
    params, ready, preview, loading, error, refresh, offset, setOffset };
};